import { createAsyncThunk, createEntityAdapter, createSelector, createSlice } from '@reduxjs/toolkit';
import { RootState } from 'Douleutaras/redux/store';
import { JSONApiDataObject, ReduxEntity } from 'Douleutaras/types';
import { Category, CategoryVatRate } from 'Douleutaras/types/category';
import { makeRequest } from 'Douleutaras/services/apiClient';

const CATEGORY_API = 'api/public/category';

const categoriesAdapter = createEntityAdapter<JSONApiDataObject<Category>>({
  selectId: cat => cat.id
});

export const getCategoryVat = createAsyncThunk<{ catId: string; data: Array<CategoryVatRate> }, { catId: string }>(
  'get/category/vat',
  async ({ catId }, api) => {
    const response = await makeRequest(
      `${CATEGORY_API}/${catId}/vat_rates/`,
      {
        method: 'GET'
      }
    )(api);

    return {
      catId,
      data: response.data
    };
  }
);

export const categoriesSlice = createSlice({
  name: 'categories',
  initialState: categoriesAdapter.getInitialState({
    loading: false,
    error: false,
    meta: {},
    entities: {}
  } as ReduxEntity<JSONApiDataObject<Category>>),
  reducers: {
    categoriesAdded: (state, action) => {
      categoriesAdapter.setMany(state, action.payload);
    }
  },
  extraReducers: builder => {
    builder.addCase(getCategoryVat.fulfilled, (state, action) => {
      state.entities[action.payload.catId].attributes.availableVatRates = action.payload.data;
    });
  }
});

export const selectCategoryByBidId = createSelector([
  state => state.jobBids,
  state => state.jobs,
  state => state.categories,
  (state, { bidId }) => ({ bidId })
], (bids, jobs, categories, { bidId }) => {
  const bid = bids.entities[bidId];
  if (!bid || !bid.relationships.job) {
    return;
  }
  const job = jobs.entities[bid.relationships.job.data.id];
  if (!job || !job.relationships.category) {
    return;
  }

  return categories.entities[job.relationships.category.data.id];
});

export const categoriesSelector = categoriesAdapter.getSelectors((state: RootState) => state.categories);
